import type { StoreApi } from 'zustand'
import {
  completeInitialProfileSession,
  createInitialProfileSession,
  updateInitialProfileSession,
  type InitialProfileSession,
} from '@/features/pretest'
import type { AppState } from '@/store/rootTypes'
import { activeFieldsFromWorkspace, activeWorkspacePatch } from '@/store/workspace'

type SetState = StoreApi<AppState>['setState']
type GetState = StoreApi<AppState>['getState']

function replaceSession(sessions: InitialProfileSession[], next: InitialProfileSession) {
  return sessions.some((session) => session.id === next.id)
    ? sessions.map((session) => session.id === next.id ? next : session)
    : [...sessions, next]
}

export function createPretestSlice(set: SetState, get: GetState): Pick<AppState,
  'startInitialProfileSession' | 'saveInitialProfileSessionPosition' | 'saveInitialProfileAbilityAnswer' |
  'saveInitialProfilePreferenceAnswer' | 'completeInitialProfileSession'> {
  return {
    startInitialProfileSession: () => {
      const state = get()
      if (!state.activeStudentId) throw new Error('Cannot start a pretest without an active student.')
      const account = state.studentIndex.find((student) => student.id === state.activeStudentId)
      if (!account) throw new Error(`Student ${state.activeStudentId} was not found.`)
      const workspace = state.workspaces[state.activeStudentId]
      const existingDraft = [...(workspace?.initialProfileSessions ?? [])].reverse().find((session) => session.status === 'draft')
      if (existingDraft) return existingDraft
      const session = createInitialProfileSession({
        studentId: account.id,
        grade: account.grade === 'G4' ? account.grade : 'G4',
        mode: workspace?.profile ? 'diagnostic' : 'baseline',
      })
      const patch = { initialProfileSessions: replaceSession(workspace?.initialProfileSessions ?? [], session) }
      set((current) => ({
        ...activeWorkspacePatch(current, patch),
        ...patch,
      }))
      return session
    },
    saveInitialProfileSessionPosition: ({ sessionId, phase, questionIndex }) => {
      let updatedSession: InitialProfileSession | null = null
      set((state) => {
        if (!state.activeStudentId) return state
        const workspace = state.workspaces[state.activeStudentId]
        const session = workspace?.initialProfileSessions.find((item) => item.id === sessionId)
        if (!session || session.status === 'completed') return state
        const next = updateInitialProfileSession(session, { phase, questionIndex })
        updatedSession = next
        const patch = { initialProfileSessions: replaceSession(workspace.initialProfileSessions, next) }
        return { ...activeWorkspacePatch(state, patch), ...patch }
      })
      return updatedSession
    },
    saveInitialProfileAbilityAnswer: ({ sessionId, questionIndex, record, phase = 'ability', unobservedReason }) => {
      let updatedSession: InitialProfileSession | null = null
      set((state) => {
        if (!state.activeStudentId) return state
        const workspace = state.workspaces[state.activeStudentId]
        const session = workspace?.initialProfileSessions.find((item) => item.id === sessionId)
        if (!session || session.status === 'completed') return state
        const next = updateInitialProfileSession(session, {
          phase,
          questionIndex,
          abilityAnswers: { ...session.abilityAnswers, [record.questionId]: record },
          unobservedReasons: unobservedReason
            ? { ...session.unobservedReasons, [record.questionId]: unobservedReason }
            : session.unobservedReasons,
        })
        updatedSession = next
        const patch = { initialProfileSessions: replaceSession(workspace.initialProfileSessions, next) }
        return { ...activeWorkspacePatch(state, patch), ...patch }
      })
      return updatedSession
    },
    saveInitialProfilePreferenceAnswer: ({ sessionId, questionId, questionIndex, answerIndex }) => {
      let updatedSession: InitialProfileSession | null = null
      set((state) => {
        if (!state.activeStudentId) return state
        const workspace = state.workspaces[state.activeStudentId]
        const session = workspace?.initialProfileSessions.find((item) => item.id === sessionId)
        if (!session || session.status === 'completed') return state
        const next = updateInitialProfileSession(session, {
          phase: 'preference',
          questionIndex,
          preferenceAnswers: { ...session.preferenceAnswers, [questionId]: answerIndex },
        })
        updatedSession = next
        const patch = { initialProfileSessions: replaceSession(workspace.initialProfileSessions, next) }
        return { ...activeWorkspacePatch(state, patch), ...patch }
      })
      return updatedSession
    },
    completeInitialProfileSession: (sessionId, result) => {
      const state = get()
      const studentId = state.activeStudentId
      if (!studentId) return { completed: false, baselineApplied: false, session: null }
      const workspace = state.workspaces[studentId]
      const session = workspace?.initialProfileSessions.find((item) => item.id === sessionId)
      if (!session) return { completed: false, baselineApplied: false, session: null }
      const baselineApplied = session.mode === 'baseline' && !workspace.profile
      if (baselineApplied) {
        get().setInitialProfile({
          id: studentId,
          name: state.student.name,
          grade: 'G4',
          avatar: state.student.avatar,
        }, result)
      }
      const completed = completeInitialProfileSession(session, result)
      set((current) => {
        const currentWorkspace = current.workspaces[studentId]
        if (!currentWorkspace) return current
        const patch = { initialProfileSessions: replaceSession(currentWorkspace.initialProfileSessions, completed) }
        return {
          ...activeWorkspacePatch(current, patch),
          ...patch,
          ...(current.activeStudentId === studentId ? activeFieldsFromWorkspace({ ...currentWorkspace, ...patch }) : {}),
        }
      })
      return { completed: true, baselineApplied, session: completed }
    },
  }
}
